/**
 * Display-only currency conversion for dashboard estimates, driven by the
 * desk's published FX rates (GET /fxrates). Mirrors the mobile app: a holding
 * is converted straight from its own currency into the display currency —
 * BASE→QUOTE at the bid, QUOTE→BASE at 1/ask — rather than round-tripping
 * through a USD valuation, so an AMD position shown in AMD is exact.
 */

export interface FxRateLike {
  base: string;
  quote: string;
  ask: number | string;
  bid: number | string;
}

/** Units of `to` per 1 unit of `from`, keyed `table[FROM][TO]`. */
export type FxRateTable = Record<string, Record<string, number>>;

/**
 * Minor units with no rate row of their own. Staff are served the desk's
 * stored rows only (the backend derives GBX for clients), so the admin applies
 * the same mapping as the backend's SettlementCurrency: GBX = GBP / 100.
 */
const MINOR_UNITS: Record<string, { major: string; scale: number }> = {
  GBX: { major: 'GBP', scale: 100 },
};

/** Cross rates are tried through USD first so the result never depends on row order. */
const PREFERRED_PIVOT = 'USD';

function positive(value: number | string): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function buildFxRateTable(
  rows: FxRateLike[] | null | undefined
): FxRateTable {
  const table: FxRateTable = {};
  const set = (from: string, to: string, rate: number) => {
    (table[from] ??= {})[to] = rate;
  };

  for (const r of rows ?? []) {
    const base = String(r?.base ?? '').toUpperCase();
    const quote = String(r?.quote ?? '').toUpperCase();
    if (!base || !quote || base === quote) continue;
    const bid = positive(r.bid);
    const ask = positive(r.ask);
    if (bid) set(base, quote, bid);
    if (ask) set(quote, base, 1 / ask);
  }

  // One-hop crosses for pairs no row covers, read off the direct rates only.
  const direct = Object.fromEntries(
    Object.entries(table).map(([from, tos]) => [from, { ...tos }])
  );
  const currencies = Object.keys(direct);
  const pivots = [
    PREFERRED_PIVOT,
    ...currencies.filter((c) => c !== PREFERRED_PIVOT).sort(),
  ];
  for (const from of currencies) {
    for (const to of currencies) {
      if (from === to || table[from]?.[to]) continue;
      for (const pivot of pivots) {
        const leg1 = direct[from]?.[pivot];
        const leg2 = direct[pivot]?.[to];
        if (leg1 && leg2) {
          set(from, to, leg1 * leg2);
          break;
        }
      }
    }
  }
  return table;
}

/** Units of `to` per 1 `from`, or null when no rate is published for the pair. */
export function fxRate(
  table: FxRateTable | null | undefined,
  from: string | null | undefined,
  to: string | null | undefined
): number | null {
  let src = (from || '').toUpperCase();
  let dst = (to || '').toUpperCase();
  if (!src || !dst) return null;

  let factor = 1;
  const srcMinor = MINOR_UNITS[src];
  if (srcMinor) {
    src = srcMinor.major;
    factor /= srcMinor.scale;
  }
  const dstMinor = MINOR_UNITS[dst];
  if (dstMinor) {
    dst = dstMinor.major;
    factor *= dstMinor.scale;
  }

  if (src === dst) return factor;
  const rate = table?.[src]?.[dst];
  return rate ? rate * factor : null;
}

/** `amount` of `from` restated in `to`; null when there is no rate. */
export function convertAmount(
  table: FxRateTable | null | undefined,
  amount: number | null | undefined,
  from: string | null | undefined,
  to: string | null | undefined
): number | null {
  const rate = fxRate(table, from, to);
  if (rate === null) return null;
  return (Number(amount) || 0) * rate;
}

function formatNumber(amount: number, digits: number): string {
  return Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** `$1,234.56` for USD, `1,234.56 AMD` otherwise. */
export function formatCurrencyAmount(
  amount: number,
  currency: string,
  digits = 2
): string {
  const sign = amount < 0 ? '-' : '';
  const n = formatNumber(amount, digits);
  return currency === 'USD' ? `${sign}$${n}` : `${sign}${n} ${currency}`;
}

/** Signed P&L: `+$17.39` for USD (as formatPnl), `+17.39 AMD` otherwise. */
export function formatCurrencyPnl(
  amount: number,
  currency: string,
  digits = 2
): string {
  const sign = amount < 0 ? '-' : '+';
  const n = formatNumber(amount, digits);
  return currency === 'USD' ? `${sign}$${n}` : `${sign}${n} ${currency}`;
}
